import React, { useEffect, useState, useCallback } from 'react';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AllianceCard from 'components/AllianceCard';
import Header from 'components/AppBar';
import Button from 'components/Buttons/Button';
import DeleteButton from 'components/Buttons/DeleteButton';
import BottomSheet from 'components/ConfirmBottomSheet';
import FieldWithoutGrid from 'components/FieldWithoutGrid';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Loyalty } from 'services/loyaltiesService';
import { htmlT, padLeadingZeros } from 'utils/generic';

import {
  Container,
  StyledGallery,
  StepperContainer,
  Stepper,
  FormContainer,
  CheckBoxContainer,
  CheckBoxStyled,
  CheckLabel,
  GroupButton,
  MainContainer,
  DeleteButtonContainer,
  SaveChangesContainer,
} from './style';

interface Props {
  show: boolean;
  userId?: string;
  idSelected: string;
  loyalties: Loyalty[];
  update: (loyalty: Loyalty) => Promise<void>;
  remove: (loyaltyId: string) => Promise<void>;
  updatePreference: (loyalty: Loyalty, loyaltyPreference: Loyalty) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

const EditLoyalty = ({
  show,
  idSelected,
  loyalties,
  userId,
  update,
  updatePreference,
  remove,
  onBack,
  isSubmitting,
}: Props) => {
  const [showSave, setShowSave] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSaveAlterPreference, setShowSaveAlterPreference] = useState(false);

  const [index, setIndex] = useState(-1);
  const [isChange, setIsChange] = useState(false);
  const [selectedLoyalty, setSelectedLoyalty] = useState<Loyalty>();
  const [isChecked, setCheckedState] = useState(false);
  const { t } = useStoreTranslation();
  const [saveLoyaltyValues, setSaveLoyaltyValues] = useState({} as Loyalty);
  const [saveLoyaltyAlterPreference, setSaveLoyaltyAlterPreference] = useState({} as Loyalty);

  const [initValues, setInitValues] = useState({
    id: selectedLoyalty?.id || '',
    userId,
    loyaltyId: selectedLoyalty?.loyaltyId || '',
    loyaltyNumber: selectedLoyalty?.loyaltyNumber || '',
    useLoyaltyNumberIfSameAlliance: selectedLoyalty?.loyaltyName || false,
  });

  const validationSchema = yup.object().shape({
    loyaltyNumber: yup.string().trim().max(100).required(),
  });

  useEffect(() => {
    setSelectedLoyalty(loyalties[index]);
  }, [index, loyalties]);

  useEffect(() => {
    if (index >= 0 || !show) return;

    if (loyalties.length > 0) {
      const selectedIndex = loyalties.findIndex((x) => x.id === idSelected);
      if (selectedIndex >= 0) {
        setIndex(selectedIndex);
      } else {
        setIndex(0);
      }
    }
  }, [idSelected, loyalties, index, show]);

  useEffect(() => {
    const converted =
      selectedLoyalty?.loyaltyId === 'AV'
        ? isNaN(Number(selectedLoyalty?.loyaltyNumber))
          ? selectedLoyalty?.loyaltyNumber ?? ''
          : `${Number(selectedLoyalty?.loyaltyNumber)}`
        : selectedLoyalty?.loyaltyNumber ?? '';
    setInitValues({
      id: selectedLoyalty?.id || '',
      userId,
      loyaltyId: selectedLoyalty?.loyaltyId || '',
      loyaltyNumber: converted,
      useLoyaltyNumberIfSameAlliance: selectedLoyalty?.useLoyaltyNumberIfSameAlliance || false,
    });
    setCheckedState(selectedLoyalty?.useLoyaltyNumberIfSameAlliance || false);
    setIsChange(false);
  }, [userId, selectedLoyalty]);

  const handleBack = useCallback(() => {
    onBack();
    setIndex(-1);
  }, [onBack]);

  const handleSelect = useCallback((i: number) => {
    setIndex(i);
  }, []);

  const handleCheckBox = useCallback((isCheck: boolean) => {
    setCheckedState(isCheck);
    setIsChange(true);
  }, []);

  const handleSubmit = useCallback(
    (value: Loyalty) => {
      setSaveLoyaltyValues({
        id: value.id,
        userId,
        loyaltyId: value.loyaltyId,
        loyaltyNumber: value.loyaltyId === 'AV' ? padLeadingZeros(value.loyaltyNumber, 11) : value.loyaltyNumber,
        useLoyaltyNumberIfSameAlliance: isChecked,
      } as Loyalty);

      if (isChecked) {
        const findAlter = loyalties.find(
          (x) =>
            x.useLoyaltyNumberIfSameAlliance && x.id !== value.id && x.allianceName === selectedLoyalty?.allianceName,
        );
        if (findAlter) {
          setShowSaveAlterPreference(true);
          setSaveLoyaltyAlterPreference({
            id: findAlter.id,
            userId,
            loyaltyName: findAlter.loyaltyName,
            loyaltyId: findAlter.loyaltyId,
            loyaltyNumber: findAlter.loyaltyNumber,
            program: findAlter.program,
            useLoyaltyNumberIfSameAlliance: false,
          } as Loyalty);
        } else {
          setShowSave(true);
        }
      } else {
        setShowSave(true);
      }
    },
    [isChecked, loyalties, userId, selectedLoyalty],
  );

  const handleSave = useCallback(async () => {
    await update(saveLoyaltyValues);
    handleBack();
  }, [saveLoyaltyValues, handleBack, update]);

  const handleDelete = useCallback(async () => {
    await remove(selectedLoyalty?.id || '');
    if (!loyalties[index + 1]) setIndex(index - 1);
  }, [remove, selectedLoyalty, loyalties, index]);

  const handleAlterPreference = useCallback(() => {
    updatePreference(saveLoyaltyValues, saveLoyaltyAlterPreference);
  }, [updatePreference, saveLoyaltyValues, saveLoyaltyAlterPreference]);

  if (!show) return null;

  return (
    <MainContainer>
      <Mobile>
        <Header goBack={handleBack}>{t('travelerLoyalty.titleList')}</Header>
      </Mobile>

      <Desktop>
        <ModalHeader onClose={handleBack} title={t('travelerLoyalty.titleList')} />
      </Desktop>

      {loyalties.length > 0 && (
        <Container>
          <StyledGallery enableKeyboard={false} index={index} length={loyalties.length} onRequestChange={handleSelect}>
            {loyalties.map((loyalty) => (
              <div key={loyalty.id}>
                <AllianceCard
                  allianceName={loyalty.loyaltyName}
                  type={loyalty.type}
                  logo={loyalty.logo}
                  initialColor={loyalty.color.split(';')[0]}
                  finalColor={loyalty.color.split(';')[1]}
                />
              </div>
            ))}
          </StyledGallery>
          <StepperContainer>
            {loyalties.map((_, i) => {
              const indexTens = Math.trunc(index / 10);
              const active = indexTens > 0 ? index - indexTens * 10 : index;

              if (i < 10) {
                return <Stepper key={i.toString()} isActive={i === active} />;
              } else {
                return <></>;
              }
            })}
          </StepperContainer>
          <Formik
            enableReinitialize
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues as Loyalty}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            <FormContainer>
              <FieldWithoutGrid
                simpleTooltip
                simpleTooltipText={t('loyaltyForm.tooltipLoyaltyNumber')}
                name="loyaltyNumber"
                label={t('loyaltyForm.loyaltyNumberLabel')}
              >
                <Input
                  autoComplete="off"
                  onChange={() => {
                    setIsChange(true);
                  }}
                  placeholder={t('loyaltyForm.loyaltyNumber')}
                />
              </FieldWithoutGrid>
              {selectedLoyalty?.allianceName && (
                <CheckBoxContainer>
                  <CheckBoxStyled circle checked={isChecked} onChange={handleCheckBox}>
                    <CheckLabel>
                      {t('loyaltyForm.checkLabelAlliance', {
                        allianceName: selectedLoyalty.allianceName,
                        name: selectedLoyalty.loyaltyName,
                      }).replace('&amp;', '&')}
                    </CheckLabel>
                  </CheckBoxStyled>
                </CheckBoxContainer>
              )}
              <GroupButton>
                <DeleteButtonContainer>
                  <DeleteButton value={t('loyaltyForm.delete')} onClick={() => setShowDelete(true)} />
                </DeleteButtonContainer>
                <SaveChangesContainer visible={isChange}>
                  <Button disabled={false} value={t('loyaltyForm.saveChanges')} />
                </SaveChangesContainer>
              </GroupButton>
            </FormContainer>
          </Formik>
        </Container>
      )}
      <BottomSheet
        show={showSave}
        title={t('loyaltyForm.saveChanges')}
        content={t('loyaltyForm.saveContent')}
        onClose={() => setShowSave(false)}
        onConfirm={handleSave}
        confirmLoading={isSubmitting}
        autoClose={false}
      />
      <BottomSheet
        show={showDelete}
        title={t('loyaltyForm.delete')}
        content={t('loyaltyForm.deleteContent')}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        autoClose={false}
      />
      <BottomSheet
        show={showSaveAlterPreference}
        title={t('loyaltyForm.saveAlterPreference')}
        content={htmlT(t, 'loyaltyForm.saveAlterPreferenceContent', {
          current: saveLoyaltyAlterPreference?.program || '',
          new: selectedLoyalty?.allianceName || '',
        })}
        onClose={() => setShowSaveAlterPreference(false)}
        onConfirm={handleAlterPreference}
        confirmLoading={isSubmitting}
        autoClose={false}
      />
    </MainContainer>
  );
};

export default EditLoyalty;
